<script>
  import { get } from 'svelte/store';
  import { t } from '../i18n/index.js';
  import { showScreen, uploadProgress, uploadStatus } from '../stores/screen.js';
  import { scannedPages, removePage, reorderPages, clearPages, MAX_PAGES } from '../stores/pages.js';
  import { session, isRecovering, subscription, incrementUploadsUsed } from '../stores/session.js';
  import { imagesToPdf, generateFileName } from '../pdf.js';
  import { uploadToR2, uploadViaWebSocket, validateSession } from '../storage.js';
  import { getWebSocket, getDesktopChannel } from '../lib/session-health.js';
  import { scanMode } from '../stores/settings.js';
  import { trackEvent } from '../utils/analytics.js';

  import PageGrid from '../components/thumbnails/PageGrid.svelte';
  import PageEditModal from '../components/thumbnails/PageEditModal.svelte';

  let editingPage = $state(null);
  let showEditModal = $state(false);
  let showLimitDialog = $state(false);

  function handlePageTap(page) {
    editingPage = page;
    showEditModal = true;
  }

  function handlePageDelete(pageId) {
    removePage(pageId);
    if ($scannedPages.length === 0) {
      showScreen('camera');
    }
  }

  function handleReorder(fromIndex, toIndex) {
    reorderPages(fromIndex, toIndex);
  }

  function handleEditSave(updates) {
    // Update page with new filter/corners
    if (editingPage) {
      const index = $scannedPages.findIndex(p => p.id === editingPage.id);
      if (index >= 0) {
        scannedPages.update(pages => {
          pages[index] = { ...pages[index], ...updates };
          return pages;
        });
      }
    }
    showEditModal = false;
    editingPage = null;
  }

  function handleEditCancel() {
    showEditModal = false;
    editingPage = null;
  }

  function handleAddPage() {
    if ($scannedPages.length >= MAX_PAGES) {
      // Show max pages toast
      return;
    }
    showScreen('camera');
  }

  function closeLimitDialog() {
    showLimitDialog = false;
  }

  async function handleSend() {
    if ($scannedPages.length === 0) return;

    // Block upload while session is being validated after background return
    if (get(isRecovering)) {
      console.log('[Upload] Blocked — session validation in progress');
      return;
    }

    // Check upload limit (client-side pre-check for Free users)
    const sub = get(subscription);
    if (sub.dailyUploadLimit > 0 && sub.dailyUploadsUsed >= sub.dailyUploadLimit) {
      showLimitDialog = true;
      return;
    }

    // Check session exists
    const pairingInfo = get(session);
    if (!pairingInfo) {
      showScreen('error');
      return;
    }

    showScreen('uploading');
    uploadProgress.set(0);
    uploadStatus.set(t('upload.converting'));

    try {
      // Convert pages to PDF (use thumbnail which has filter applied, or fullImage)
      const pages = $scannedPages.map(p => p.thumbnail || p.fullImage);
      const pdfBlob = await imagesToPdf(pages);
      const fileName = generateFileName();

      uploadProgress.set(10);
      uploadStatus.set(t('upload.sending'));

      // Validate session before upload
      const sessionCheck = await validateSession();
      if (!sessionCheck.valid) {
        if (sessionCheck.requiresRepairing) {
          showScreen('pairing');
          return;
        }
        throw new Error(sessionCheck.error || 'Session invalid');
      }

      // Use WS relay only for Web Desktop, R2 for Flutter Desktop
      let result;
      const ws = getWebSocket();
      const isWebDesktop = getDesktopChannel() === 'web';
      if (isWebDesktop && ws && ws.readyState === WebSocket.OPEN) {
        try {
          console.log('[Upload] Using WebSocket relay (Web Desktop)');
          result = await uploadViaWebSocket(pdfBlob, fileName, (progress) => {
            uploadProgress.set(progress);
          });
          console.log('[Upload] WebSocket relay succeeded');
        } catch (wsError) {
          console.log('[Upload] WebSocket relay failed, falling back to R2:', wsError);
          result = await uploadToR2(pdfBlob, fileName, (progress) => {
            uploadProgress.set(progress);
          });
        }
      } else {
        console.log(`[Upload] Using R2 (desktop channel: ${getDesktopChannel() || 'flutter'})`);
        result = await uploadToR2(pdfBlob, fileName, (progress) => {
          uploadProgress.set(progress);
        });
      }

      // Track scan completion with properties
      const filters = $scannedPages.map(p => p.filter).filter(Boolean);
      const primaryFilter = filters.length > 0
        ? filters.sort((a, b) => filters.filter(f => f === b).length - filters.filter(f => f === a).length)[0]
        : 'none';
      trackEvent('scan_submitted', {
        photos_count: $scannedPages.length,
        filter: primaryFilter,
        mode: $scanMode,
      });

      // Success — optimistically increment upload count
      incrementUploadsUsed();
      clearPages();
      showScreen('complete');

    } catch (error) {
      console.error('Upload failed:', error);
      if (error.requiresRepairing) {
        showScreen('pairing');
      } else {
        showScreen('error');
      }
    }
  }
</script>

<div class="screen flex flex-col h-full">
  <header class="p-4 text-center border-b border-gray-700">
    <h1 class="text-xl font-semibold">{t('multipage.title')}</h1>
    <p class="text-gray-400 text-sm mt-1">
      {t('multipage.count', { count: $scannedPages.length })}
    </p>
  </header>

  <main class="flex-1 overflow-hidden">
    <PageGrid
      pages={$scannedPages}
      onTap={handlePageTap}
      onDelete={handlePageDelete}
      onReorder={handleReorder}
    />
  </main>

  <footer class="p-4 flex gap-4 bg-gray-900/95 backdrop-blur safe-area-bottom">
    <button
      onclick={handleSend}
      disabled={$scannedPages.length === 0}
      class="flex-1 btn-large py-4 px-6 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-medium transition-colors"
    >
      {t('multipage.send')}
    </button>
    <button
      onclick={handleAddPage}
      disabled={$scannedPages.length >= MAX_PAGES}
      class="flex-1 btn-large py-5 px-6 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-bold transition-colors text-xl"
    >
      {t('multipage.addPage')}
    </button>
  </footer>
</div>

{#if showEditModal && editingPage}
  <PageEditModal
    page={editingPage}
    onSave={handleEditSave}
    onCancel={handleEditCancel}
  />
{/if}

{#if showLimitDialog}
  <!-- svelte-ignore a11y_interactive_supports_focus -->
  <div class="limit-overlay" role="dialog" aria-modal="true" onclick={closeLimitDialog} onkeydown={(e) => e.key === 'Escape' && closeLimitDialog()}>
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <div class="limit-dialog" role="document" onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.stopPropagation()}>
      <div class="limit-icon">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 class="text-xl font-bold mb-2">{t('upload.limitReached.title')}</h3>
      <p class="text-gray-400 whitespace-pre-line mb-6">{t('upload.limitReached.message')}</p>
      <button
        onclick={closeLimitDialog}
        class="w-full py-3 px-6 bg-blue-600 hover:bg-blue-500 rounded-xl font-medium text-lg transition-colors"
      >
        {t('upload.limitReached.ok')}
      </button>
    </div>
  </div>
{/if}

<style>
  .screen {
    display: flex;
  }

  .btn-large {
    min-height: 60px;
    font-size: 1.125rem;
  }

  .safe-area-bottom {
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }

  .limit-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
    padding: 1.5rem;
  }

  .limit-dialog {
    background: #1f2937;
    border-radius: 1rem;
    padding: 2rem;
    text-align: center;
    max-width: 320px;
    width: 100%;
  }

  .limit-icon {
    margin-bottom: 1rem;
    display: flex;
    justify-content: center;
  }
</style>
